import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'axios';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import { Modal } from 'components/common/modal';
import { closeModal } from 'features/modalSlice';

import { Checkbox } from 'components/common/checkbox';
import { Input } from 'components/common/input';
import { BarsLoadingIndicator } from 'components/common/barsLoadingIndicator';

import { apiConfig, COOKIE_NAME } from '../../config';
import { useGetBookingData } from 'hooks';
import { getCookie, applyImportantStyles } from 'lib';

import './TrackPricesModal.css';

const TrackPrices = () => {
    const dispatch = useDispatch();

    const bookingRefText = useSelector(
        (state) => state.miniBookingWidget?.data?.bookingRefText
    );
    const colors = useSelector(
        (state) => state.miniBookingWidget?.data?.colors
    );
    const { isTrackPricesModalOpen } = useSelector((state) => state.modal);
    const data = useSelector((state) => state.miniBookingWidget?.data);
    const Lang = data?.Lang;
    const propertyID = data?.propertyID;
    const ourPrice = data?.ourPrice;
    const cookiepolicyLink = data?.cookiepolicyLink;

    const [email, setEmail] = useState('');
    const [acceptEmailNotifications, setAcceptEmailNotifications] =
        useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const { userBookingData } = useGetBookingData();

    const isMultiproperty = useSelector(
        (state) => state.user?.isMultipropertyTrackPrices === true
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateInputs();

        if (!isEmpty(errors)) {
            setErrors(errors);
            return;
        }

        const cookie = getCookie(COOKIE_NAME);
        // let bookLink = `${apiConfig.reserveOnlineUrl}?src=470^adults=${userBookingData?.adults}`;
        // bookLink += `^children=${userBookingData?.children}^lang=${userBookingData?.localeLanguage}`;
        // bookLink += `^currency=${userBookingData?.currency}^checkin=${userBookingData?.checkin}`;
        // bookLink += `^checkout=${userBookingData?.checkout}^nights=0`;

        try {
            setIsLoading(true);
            const response = await axios.post(apiConfig?.trackPrices, {
                ...userBookingData,
                curCode: userBookingData?.currency,
                lang: userBookingData?.localeLanguage,
                email,
                cookie,
                propertyID,
                price: ourPrice?.price,
                // booklink: bookLink,
                isMultiproperty: isMultiproperty,
            });

            if (response?.data?.toLowerCase() === 'ok') {
                toast.success(Lang?.verifyEmailMsg, {
                    autoClose: true,
                    theme: 'colored',
                });
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
        }

        handleModalClose();
        setIsLoading(false);
    };

    const handleModalClose = useCallback(() => {
        dispatch(closeModal({ modalId: 'TrackPricesModal' }));
        setErrors({});
    }, [dispatch, closeModal, setErrors]);

    const validateInputs = () => {
        const errors = {};

        const emailRegex =
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!email) {
            errors.email = Lang?.fillInYourEmail;
        } else if (!email.match(emailRegex)) {
            errors.email = Lang?.pleaseEnterValidEmail;
        }

        if (!acceptEmailNotifications) {
            errors.acceptEmailNotifications = Lang?.acceptTermsAndConditions;
        }

        return errors;
    };

    const renderContent = () => {
        return (
            <div className="ab-track-prices-cnt">
                <p className="ab-track-prices">{bookingRefText}</p>

                <form
                    className="ab-track-prices-form"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="ab-track-prices-input-group">
                        <div className="ab-track-prices-input">
                            <Input
                                type="email"
                                placeholder={Lang?.yourEmailAddress}
                                value={email}
                                onChange={(event) =>
                                    setEmail(event.target.value)
                                }
                                isRequired
                                isInvalid={Boolean(errors?.email)}
                                errorMsg={errors.email}
                                id="track-prices-email"
                                autoFocus
                                ref={(el) => {
                                    applyImportantStyles(el, {
                                        border: '1px solid #ccc !important;',
                                        padding: '10px !important;',
                                    });
                                }}
                            />
                        </div>
                    </div>

                    <div className="ab-track-prices-input-group">
                        <div className="ab-track-prices-input">
                            <Checkbox
                                isChecked={acceptEmailNotifications}
                                onChange={(event) =>
                                    setAcceptEmailNotifications(
                                        event.target.checked
                                    )
                                }
                            >
                                {Lang?.receiveEmailNotification}
                            </Checkbox>
                        </div>

                        {errors?.acceptEmailNotifications && (
                            <div className="ab-track-prices-input-error">
                                {<span>{errors.acceptEmailNotifications}</span>}
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        style={{
                            backgroundColor: colors?.primaryBackgroundColor,
                            color: colors?.primaryTextColor,
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <BarsLoadingIndicator />
                        ) : (
                            Lang?.priceTracking
                        )}
                    </button>
                </form>

                <p className="ab-track-prices-privacy-policy">
                    {Lang?.bySubscribing}{' '}
                    <a href={cookiepolicyLink} target="_blank" rel="noreferrer">
                        {Lang?.privacyPolicy}
                    </a>
                </p>
            </div>
        );
    };

    return (
        <Modal
            className="ab-modal--track-prices"
            color={colors?.primaryTextColor}
            backgroundColor={colors?.primaryBackgroundColor}
            icon="MdNotificationsActive"
            isOpen={isTrackPricesModalOpen}
            onClose={handleModalClose}
            onOverlayClick={handleModalClose}
            placement="center"
            renderContent={renderContent}
            styles={{
                closeIcon: { color: colors?.primaryTextColor },
            }}
            title={Lang?.trackPrices}
            variant="secondary"
            width={300}
        />
    );
};

export default TrackPrices;
